import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DataGridPremium,
  DataGridPremiumProps,
  GRID_AGGREGATION_FUNCTIONS,
  GridCellEditStopReasons,
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridColumnsInitialState,
  GridRowOrderChangeParams,
  GridRowsProp,
  GridValidRowModel,
  MuiBaseEvent,
  MuiEvent,
  useGridApiRef,
  GridCallbackDetails,
  GridCellMode,
  GridRow,
  GridRowProps,
} from '@mui/x-data-grid-premium';
import { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium';
import { GridSortModel } from '@mui/x-data-grid/models/gridSortModel';
import { GridRowClassNameParams } from '@mui/x-data-grid/models/params';
import { GridRowParams } from '@mui/x-data-grid/models/params/gridRowParams';
import React, {
  FC,
  MutableRefObject,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import equal from 'fast-deep-equal/react';
import { useTranslation } from 'react-i18next';
import { BehaviorSubject, debounceTime, filter, Subscription } from 'rxjs';
import { EmptyTable, IEmptyTable } from './components/common.components.tsx';
import { Search } from './components/search.component.tsx';
import { GridCellParams } from '@mui/x-data-grid/models/params/gridCellParams';
import { SxProps } from '@mui/system';
import { setCellStyles, setRowStyles } from './styles/style.actions.ts';
import { GridRowModel } from '@mui/x-data-grid/models/gridRows';
import { IUseTabNavigation, useTabNavigation } from './hooks/useTabNavigation.tsx';
import { useControlScrollClick } from './hooks/useControlScrollClick.tsx';
import { formColumnsAndFooter, isEditModeEntryKey } from './functions/sharedFunc.ts';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { useCustomArrowNavigation } from './hooks/useCustomArrowNavigation.tsx';
import { isEnterKey } from '../../helpers/utils/utils.helper.ts';
import { useSafariArrowScrollFix } from './hooks/useSafariArrowScrollFix.tsx';
import { Loading } from './components/loading.component.tsx';
import { MessageRotatorProps } from '../messageRotator/messageRotator.component';
import { useSmartVerticalDividers } from './hooks/useSmartVerticalDividers.ts';

/* Hard cap on the WA-3671 keystroke guard, in case the requested row never
arrives (rejected add, failed refetch): the grid must not stay deaf. Generously
above a real handoff, which is one state tick plus one grid render. */
const ROW_FOCUS_HANDOFF_TIMEOUT = 500;

const CustomGridRow = (
  props: GridRowProps & {
    getRowDataAttr?: (row: GridValidRowModel) => string;
  },
) => {
  const { getRowDataAttr, row, ...rest } = props;
  return (
    <GridRow
      {...rest}
      row={row}
      {...(getRowDataAttr ? { 'data-attr': getRowDataAttr(row) } : {})}
    />
  );
};

export const Table: FC<TypeTable> = ({
  data,
  children,
  defaultSorting,
  onSearch,
  searchValue,
  searchStyles,
  searchWrapperStyle = {},
  viewRows,
  viewCells,
  heightOffset = 0,
  onSort: onSortModelChange,
  scrollToSelectedRow,
  sortingOrder = ['asc', 'desc'],
  onChangeColumnModel,
  onColumnWidthChange,
  onColumnOrderChange,
  height,
  detailRowContent,
  rowSelectionModel,
  replacedId,
  aggregationFunctions,
  onChangeOrderPosition,
  onRowOrderChange,
  emptyTableIndication,
  onRowsCountChange,
  childrenButtons,
  columnVisibilityModel,
  disableRowReorder = [],
  hidingColumnModel,
  onCellKeyDown,
  focusTabProps = {},
  onCellEditStop,
  onCellEditStart,
  arrowNavigation,
  externalApiRef,
  isGroupTable = false,
  customArrowHandler = false,
  isLongLoading = false,
  messagesForLongLoading,
  enableSingleClickEditing = true,
  rowDataAttr,
  ...props
}) => {
  const internalApiRef = useGridApiRef();
  const defineIsNeededDivider = useSmartVerticalDividers(viewCells);
  const apiRef: MutableRefObject<GridApiPremium> = useMemo(
    () => externalApiRef || internalApiRef,
    [externalApiRef, internalApiRef],
  );
  let columns: GridColDef[];
  let footer: ReactElement | null;

  if (isGroupTable) {
    columns = props?.columns as GridColDef[];
    footer = props?.footer as ReactElement | null;
  } else {
    const columnsAndFooter = formColumnsAndFooter({
      children,
      columnVisibilityModel,
      viewCells,
    });
    columns = columnsAndFooter?.columns;
    footer = columnsAndFooter?.footer;
  }

  const renderButtons = childrenButtons?.();
  const { t } = useTranslation();
  // WA-3633: uncontrolled single-click editing. Replaces the former
  // controlled `cellModesModel` path (useSingleClickEditing), which was
  // unsound under programmatic multi-transition — MUI's cleanup proposals
  // (computed from a stale internal ref) clobbered concurrently started edit
  // entries. Here MUI owns edit-mode state; a single click simply asks the
  // grid to start editing an editable, currently-viewed cell. The portal
  // guard ignores clicks that originate outside the cell (e.g. menu/portal
  // content re-targeted through the cell).
  const handleSingleClickEdit = useCallback(
    (params: GridCellParams, event: MuiEvent<React.MouseEvent>) => {
      if (!event.currentTarget.contains(event.target as Element)) return;
      if (params.isEditable && params.cellMode === 'view') {
        apiRef.current.startCellEditMode({ id: params.id, field: params.field });
      }
    },
    [apiRef],
  );
  const { onCellModeParams } = useControlScrollClick({ apiRef });
  const { handleTabNav } = useTabNavigation({ apiRef, params: focusTabProps });
  const { customArrowHandlerFn } = useCustomArrowNavigation({ apiRef });
  const { addPreventArrowKeyScroll, removePreventArrowKeyScroll } = useSafariArrowScrollFix();
  const [SDataGridPremium, setSDataGridPremium] = useState<typeof DataGridPremium>(DataGridPremium);
  const [_, setIsChangeOrder] = useState<boolean>(false);
  const potentialNextColumnName = useRef<string | undefined>(undefined);
  const columnState = useRef<IColumnStateFields>({
    model: undefined,
    state: true,
  });
  const prevRows = useRef(data as GridRowModel[]);
  const scrollStream$ = useMemo(() => new BehaviorSubject<number | undefined>(undefined), []);
  /* WA-3671: set while a keypress has requested a brand-new row but that row —
  and therefore the cell to focus next — does not exist yet. */
  const pendingRowFocus = useRef(false);
  const pendingRowFocusTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * Open the guarded window of a row-focus handoff.
   *
   * The cell the user just left still holds DOM focus (it is focused on
   * purpose, so the edit input blurs and saves while still mounted). Rows are
   * added asynchronously, so until the new row renders that stale cell is the
   * one MUI sees as focused — and one printable key there is enough for
   * `useGridCellEditing` to start editing it with `deleteValue: true`, wiping
   * the value just entered *and* pulling the pending focus back to it
   * (`updateStateToStartCellEditMode` ends in `setCellFocus`). The user then
   * keeps typing into the row they had already finished.
   *
   * Blurring the cell instead does not hold: MUI's focus state still points at
   * it, so the next commit re-focuses it. Keeping the focus ring where the user
   * left off is the better cue anyway — what must not happen is a keystroke
   * being turned into an edit there, which the guard on the wrapper prevents.
   */
  const beginRowFocusHandoff = useCallback(() => {
    pendingRowFocus.current = true;
    clearTimeout(pendingRowFocusTimer.current);
    pendingRowFocusTimer.current = setTimeout(() => {
      pendingRowFocus.current = false;
    }, ROW_FOCUS_HANDOFF_TIMEOUT);
  }, []);

  const endRowFocusHandoff = useCallback(() => {
    pendingRowFocus.current = false;
    clearTimeout(pendingRowFocusTimer.current);
  }, []);

  useEffect(() => endRowFocusHandoff, [endRowFocusHandoff]);

  const scrollToIndex = (sortedRows: GridValidRowModel[]) => {
    const selectedRowId = apiRef.current.state.rowSelection[0];
    const rowIndex = sortedRows.findIndex(
      (row: GridValidRowModel) => row[replacedId || 'id'] === selectedRowId,
    );
    scrollStream$.next(rowIndex);
  };

  useEffect(() => {
    let scrollToIndexSubscription: Subscription | undefined = undefined;
    if (apiRef.current !== undefined && scrollToIndexSubscription === undefined) {
      scrollToIndexSubscription = scrollStream$
        .pipe(
          filter((v) => v !== undefined),
          debounceTime(75),
        )
        .subscribe((rowIndex) => {
          apiRef?.current?.scrollToIndexes({
            rowIndex,
          });
        });
    }
    return () => {
      scrollToIndexSubscription && scrollToIndexSubscription.unsubscribe();
    };
  }, [apiRef.current]);

  useEffect(() => {
    if (viewRows || viewCells) {
      setSDataGridPremium(
        styled(DataGridPremium)(() => ({
          ...(viewRows?.styles && setRowStyles(viewRows.styles)),
          ...(viewCells?.styles && setCellStyles(viewCells.styles)),
        })) as typeof DataGridPremium,
      );
    }
  }, []);
  useEffect(() => {
    const sortedRows = apiRef?.current?.getSortedRows();
    if (sortedRows.length) {
      if (scrollToSelectedRow) {
        scrollToIndex(sortedRows);
      }
    }
    if (prevRows.current.length !== sortedRows.length) {
      onRowsCountChange?.(sortedRows, prevRows.current, apiRef, potentialNextColumnName.current);
      prevRows.current = sortedRows;
      potentialNextColumnName.current = undefined;
      // The row set has changed and `onRowsCountChange` has had its chance to
      // focus the new row, so the handoff is over either way (WA-3671).
      endRowFocusHandoff();
    }
  }, [data]);

  useEffect(() => {
    if (apiRef.current && rowSelectionModel === undefined) {
      apiRef.current.setRowSelectionModel([]);
    }
  }, [apiRef.current, rowSelectionModel]);

  useEffect(() => {
    if (props.initialState?.columns && columnState.current.state) {
      columnState.current.model = props.initialState?.columns;
    }
    apiRef.current.restoreState({ columns: columnState.current.model });
  });

  if (defaultSorting?.length) {
    props.initialState = {
      ...(props.initialState && { ...props.initialState }),
      sorting: { sortModel: defaultSorting },
    };
  }

  props.slots = {
    ...(props.slots && { ...props.slots }),
    detailPanelExpandIcon: ExpandMoreIcon,
    detailPanelCollapseIcon: ExpandLessIcon,
    footer: () => footer,
    noRowsOverlay: () => <EmptyTable {...emptyTableIndication} />,
    noResultsOverlay: () => <EmptyTable />,
    loadingOverlay: () => <Loading isLongLoading={isLongLoading} {...{ messagesForLongLoading }} />,
    ...(rowDataAttr && { row: CustomGridRow }),
  };
  props.slotProps = {
    ...(props.slotProps && { ...props.slotProps }),
    ...(rowDataAttr && {
      row: {
        ...(props.slotProps?.row || {}),
        getRowDataAttr: rowDataAttr,
      } as GridRowProps & {
        getRowDataAttr?: (row: GridValidRowModel) => string;
      },
    }),
  };

  const handleColumnChange = <A, B>(e: A, func: B) => {
    const currentStateCol =
      hidingColumnModel?.generatedModelFn?.(apiRef?.current?.exportState().columns) ||
      apiRef?.current?.exportState().columns;
    const initialFields = currentStateCol?.orderedFields || [];
    const updatedFields = [...initialFields];

    const addFieldsToFrontIfAbsent = (fields: string[], fieldsToAdd: string | string[]) => {
      const fieldsArray = Array.isArray(fieldsToAdd) ? fieldsToAdd : [fieldsToAdd];
      fieldsArray
        .slice()
        .reverse()
        .forEach((fieldToAdd) => {
          const fieldIndex = fields.indexOf(fieldToAdd);
          if (fieldIndex > -1) {
            fields.splice(fieldIndex, 1);
          }
          fields.unshift(fieldToAdd);
        });
    };

    if (hidingColumnModel?.multiSelectFields) {
      addFieldsToFrontIfAbsent(updatedFields, hidingColumnModel?.multiSelectFields);
    }
    addFieldsToFrontIfAbsent(updatedFields, '__reorder__');
    columnState.current = {
      model: { ...currentStateCol, orderedFields: updatedFields },
      state: false,
    };
    onChangeColumnModel?.({ ...currentStateCol, orderedFields: updatedFields });
    if (typeof func === 'function') func(e);
  };

  const getDetailPanelContent = detailRowContent?.content
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useCallback<NonNullable<DataGridPremiumProps['getDetailPanelContent']>>(
        (props) => React.cloneElement(detailRowContent.content, { ...props }),
        [detailRowContent?.content],
      )
    : null;

  const handleChangeData = <A, B>(e: A, func: B) => {
    const newData = structuredClone(apiRef?.current?.getSortedRows());
    if (!equal(data, newData)) {
      onChangeOrderPosition?.(newData);
    }
    if (typeof func === 'function') func(e);
  };

  const findNextRow: TypeFindNextRowFunction = (
    code: 'ArrowUp' | 'ArrowDown',
    rows: GridValidRowModel[],
    foundIndex: number,
    condition?: (row: GridRowModel) => boolean,
  ) => {
    const nextIndex = code === 'ArrowUp' ? foundIndex - 1 : foundIndex + 1;
    const nextRow = rows?.[nextIndex];
    if (typeof condition === 'function' && nextRow) {
      const status = condition(nextRow);
      if (!status) {
        return findNextRow(code, rows, nextIndex, condition);
      }
    }
    return nextRow;
  };

  const handleArrowClick = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (rowSelectionModel) {
      const rows = apiRef.current.getSortedRows();
      const columns = apiRef.current.getAllColumns();
      const foundIndex = rows.findIndex((row) => row[replacedId || 'id'] === rowSelectionModel);
      if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
        const conditionFunc =
          typeof arrowNavigation !== 'boolean' &&
          arrowNavigation &&
          Object.prototype.hasOwnProperty.call(arrowNavigation, 'condition')
            ? arrowNavigation?.condition
            : undefined;
        const nextRow = findNextRow(e.code, rows, foundIndex, conditionFunc);
        if (nextRow && typeof props.onRowClick === 'function') {
          const miuEvent = {} as React.MouseEvent<HTMLElement>;
          props.onRowClick(
            { row: nextRow, columns, id: nextRow?.[replacedId || 'id'] },
            miuEvent,
            {} as GridCallbackDetails<any>,
          );
        }
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          display: renderButtons ? 'flex' : 'block',
          justifyContent: 'space-between',
          ...searchWrapperStyle,
        }}
      >
        <Search
          data-testid='table-search'
          value={searchValue}
          onSearch={onSearch}
          sx={searchStyles}
        />
        {renderButtons}
      </Box>
      <SDataGridWrapper
        data-testid='table'
        isFooter={!!footer}
        offset={heightOffset + (!!onSearch ? 56 : 0)}
        focusCell={!!focusTabProps?.enabled}
        // Capture phase on purpose: the props-level `onCellKeyDown` below runs
        // *after* MUI's own editing listener, so vetoing there would be too
        // late to stop an edit from starting on the stale cell (WA-3671).
        onKeyDownCapture={(event) => {
          if (!pendingRowFocus.current || !isEditModeEntryKey(event)) return;
          event.preventDefault();
          event.stopPropagation();
        }}
        {...{ disableRowReorder, height, ...(arrowNavigation && { onKeyDown: handleArrowClick }) }}
      >
        <SDataGridPremium
          {...props}
          columns={columns}
          rows={data}
          rowHeight={32}
          columnHeaderHeight={36}
          disableColumnMenu
          apiRef={apiRef}
          disableMultipleRowSelection
          rowSelectionModel={rowSelectionModel}
          onCellKeyDown={(params, event, add) => {
            if (event && isEnterKey(event)) {
              if (params.cellMode === 'edit') {
                // By the time this fires, MUI's own useGridEditing keydown
                // listener (subscribed earlier than this one) has already run
                // its native Enter handling and stopped+committed the cell —
                // this explicit stopCellEditMode is a defensive duplicate that
                // collapses into that same edit->view transition, not a
                // second/independent stop. What this block still does own:
                // focusing the cell element first so onBlur fires on the input
                // while still mounted (value saved), which lets
                // enterKeyNavigation.handleEnterKey call setCellFocus
                // synchronously without any delays, same pattern as arrows.
                // WA-3633: this ordering — MUI's own stop+commit resolves
                // strictly before any handler registered here runs — is what
                // the produced-quantity Enter-advance macrotask relies on to
                // be race-free (see advanceToNextProducedQuantityRow).
                apiRef.current.getCellElement(params.id, params.field)?.focus({
                  preventScroll: true,
                });
                apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
              }
              event.defaultMuiPrevented = true;
            }
            // Both handlers report back when they had to request a new row
            // instead of moving focus to an existing cell.
            let rowFocusPending = !!onCellKeyDown?.(params, event, add, apiRef);
            handleTabNav(params, event);
            if (customArrowHandler) {
              const { handleNextRow, ...additionalParams } =
                typeof customArrowHandler === 'object' ? customArrowHandler : {};
              const nextColumnName = customArrowHandlerFn({
                params,
                event,
                handleNextRow,
                additionalParams,
              });
              potentialNextColumnName.current = nextColumnName;
              // `customArrowHandlerFn` only returns a column name on its
              // create-a-new-row branches (ArrowDown/ArrowRight past the last
              // row), which defer focus the same way Enter navigation does.
              rowFocusPending = rowFocusPending || !!nextColumnName;
            }
            if (rowFocusPending) beginRowFocusHandoff();
          }}
          {...(enableSingleClickEditing && {
            onCellClick: handleSingleClickEdit,
          })}
          localeText={{
            columnHeaderSortIconLabel: t('common.sort'),
          }}
          onRowOrderChange={(e) =>
            handleChangeData<GridRowOrderChangeParams, ICommonTable['onRowOrderChange']>(
              e,
              onRowOrderChange,
            )
          }
          onColumnWidthChange={(e) =>
            handleColumnChange<GridColumnResizeParams, ICommonTable['onColumnWidthChange']>(
              e,
              onColumnWidthChange,
            )
          }
          onColumnOrderChange={(e) => {
            setIsChangeOrder((prevState) => !prevState); // trigger for reorder columns updating
            handleColumnChange<GridColumnOrderChangeParams, ICommonTable['onColumnOrderChange']>(
              e,
              onColumnOrderChange,
            );
          }}
          onRowClick={(...args) => {
            props.onRowClick?.(...args);
            if (detailRowContent?.clickableRow) apiRef.current.toggleDetailPanel(args[0].id);
          }}
          sortingMode={isGroupTable ? 'client' : 'server'}
          sortingOrder={sortingOrder}
          aggregationFunctions={{ ...GRID_AGGREGATION_FUNCTIONS, ...aggregationFunctions }}
          {...(viewRows && {
            getRowClassName: (params) => viewRows.action(params, apiRef.current),
          })}
          {...(getDetailPanelContent && {
            getDetailPanelContent,
          })}
          onCellEditStart={(params, event, cb) => {
            addPreventArrowKeyScroll();
            onCellEditStart?.(params, event, cb);
          }}
          onCellEditStop={(params, event: CellEditStopEvent, cb) => {
            if (event && params.reason === GridCellEditStopReasons.cellFocusOut) {
              event.defaultMuiPrevented = true;
              const { id, field } = params;
              onCellModeParams({ id, field });
            }
            removePreventArrowKeyScroll();
            onCellEditStop?.(params, event, cb);
          }}
          getCellClassName={(params) => {
            const { id, field } = params;
            const withoutDivider = defineIsNeededDivider(params, apiRef);
            return String(`${field.replace(/\./g, '')}-${id} ${withoutDivider}`);
          }}
          {...(getDetailPanelContent && {
            getDetailPanelHeight: () => detailRowContent?.height || 'auto',
          })}
          {...(replacedId && {
            getRowId: (row) => row[replacedId],
          })}
          {...{ onSortModelChange }}
        />
      </SDataGridWrapper>
    </>
  );
};

const SDataGridWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'disableRowReorder' && prop !== 'focusCell' && prop !== 'isFooter',
})<{
  offset?: number;
  height?: number;
  disableRowReorder: string[];
  focusCell: boolean;
  isFooter: boolean;
}>(({ theme, offset, height, disableRowReorder, focusCell, isFooter }) => ({
  height: height
    ? `${height}px`
    : `calc(100vh - (49px + ${offset}px) - var(--admin-bar-height, 0px))`,
  '& .MuiDataGrid-root': {
    overflowX: 'hidden',
    '--DataGrid-containerBackground': theme.palette.grey['A100'],
    '--DataGrid-rowBorderColor': 'rgba(0, 0, 0, 0.10)',
  },
  '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--last), .MuiDataGrid-cell:not(:last-of-type):not(.MuiDataGrid-cell--editing):not(.withoutDivider)':
    {
      backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.12))',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '100% 50%',
    },
  '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--last)': {
    backgroundSize: '1px 18px',
  },
  '& .MuiDataGrid-cell:not(:last-of-type):not(.MuiDataGrid-cell--editing):not(.withoutDivider)': {
    backgroundSize: '1px calc(100% - 15px)',
  },
  '& .MuiDataGrid-row': {
    cursor: 'pointer',
  },
  '& .MuiDataGrid-cell:not([title])': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-columnHeader:focus-within:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none !important',
  },
  ...(!focusCell && {
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none !important',
    },
  }),
  '& .MuiDataGrid-overlayWrapper': {
    transition: '0.3s',
  },
  '& .MuiDataGrid-overlayWrapperInner': {
    backgroundColor: theme.palette.background.default,
    ...(isFooter && { borderBottom: `1px solid ${theme.palette.divider}` }),
  },
  '& .MuiDataGrid-row--detailPanelExpanded .MuiDataGrid-withBorderColor': {
    borderBottomColor: 'transparent',
  },
  '& .MuiDataGrid-row--lastVisible .MuiDataGrid-cell': {
    borderBottomColor: 'rgba(224, 224, 224, 1) !important',
  },
  '& .MuiDataGrid-aggregationColumnHeaderLabel': {
    display: 'none',
  },
  '& .MuiDataGrid-footerCell': {
    color: 'inherit',
  },
  '[data-id="auto-generated-group-footer-root"] > div': {
    border: 'none',
    backgroundColor: `${theme.palette.grey['100']}!important`,
    outline: 'none!important',
  },
  '& .MuiDataGrid-rowReorderCellContainer:focus, & .MuiDataGrid-rowReorderCellContainer:focus-within':
    {
      outline: 'none !important',
    },
  '& .MuiDataGrid-pinnedRows--bottom': {
    boxShadow: `0 -1px 0px 0px ${theme.palette.grey['300']}`,
    '& button, & .MuiChip-root': {
      display: 'none!important',
    },
  },
  '& .MuiDataGrid-rowReorderCell svg': {
    fill: theme.palette.grey['600'],
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: `${theme.palette.lightGrey.main}!important`,
  },
  '& .MuiDataGrid-withBorderColor': {
    borderColor: `rgba(0, 0, 0, 0.10)!important`,
  },
  '& .MuiDataGrid-iconSeparator': {
    opacity: 0,
  },

  // Fix for incorrect behaviour when we use special components for editing
  '& .MuiDataGrid-cell--editing': {
    padding: '0',
    display: 'flex',
    alignItems: 'stretch',
    overflow: 'hidden',
  },
  '& .MuiDataGrid-cell--editing .MuiFormControl-root': {
    width: '100%',
    height: '100%',
    minHeight: '100%',
    margin: '0',
    display: 'block',
  },
  '& .MuiDataGrid-cell--editing .MuiInputBase-root, & .MuiDataGrid-cell--editing .MuiOutlinedInput-root':
    {
      width: '100%',
      height: '100%',
      minHeight: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'stretch',
      padding: '0',
    },
  '& .MuiDataGrid-cell--editing .MuiInputBase-input, & .MuiDataGrid-cell--editing .MuiOutlinedInput-input, & .MuiDataGrid-cell--editing .MuiInputBase-inputSizeSmall':
    {
      boxSizing: 'border-box',
      display: 'block',
      width: '100%',
      height: '100%',
      minHeight: '100%',
      margin: '0',
      paddingTop: '0',
      paddingBottom: '0',
      lineHeight: '32px',
      transform: 'translateY(-1px)',
    },
  '& .MuiDataGrid-cell--editing fieldset, & .MuiDataGrid-cell--editing .MuiOutlinedInput-notchedOutline':
    {
      border: 'none',
    },
  ...disableRowReorder
    .map((el) => ({
      [`& .${el} .MuiDataGrid-rowReorderCell`]: {
        display: 'none!important',
      },
    }))
    .reduce((acc, curr) => ({ ...acc, ...curr }), {}),
}));
export interface ICommonTable
  extends Omit<
    DataGridPremiumProps,
    | 'rows'
    | 'columns'
    | 'sortingMode'
    | 'onSortModelChange'
    | 'initialState'
    | 'onColumnWidthChange'
    | 'getDetailPanelContent'
    | 'getDetailPanelHeight'
    | 'getRowId'
    | 'cellModesModel'
    | 'onCellModesModelChange'
    | 'onCellClick'
    | 'onCellKeyDown'
    | 'apiRef'
  > {
  children: ReactElement | ReactElement[];
  childrenButtons?: () => ReactElement<IconProps>;
  data: GridRowsProp;
  defaultSorting?: GridSortModel;
  onSearch?: (v: string) => void;
  searchValue?: string;
  searchStyles?: SxProps;
  searchWrapperStyle?: SxProps;
  onSort?: DataGridPremiumProps['onSortModelChange'];
  scrollToSelectedRow?: boolean;
  viewRows?: {
    action: (
      v: GridRowClassNameParams,
      apiRef?: MutableRefObject<GridApiPremium>['current'],
    ) => string;
    styles: IRowStyles[];
  };
  viewCells?: {
    actions: { [key in string]: (v: GridCellParams<any, any>) => string };
    styles: ICellStyles[];
  };
  replacedId?: string;
  hidingColumnModel?: {
    multiSelectFields?: string | string[]; // fields corresponding to the definition of multiselect columns at the beginning of the table
    generatedModelFn?: (model?: GridColumnsInitialState) => GridColumnsInitialState | undefined; // to create a custom column model (if there are hidden columns)
  };
  emptyTableIndication?: IEmptyTable;
  initialState?: GridInitialStatePremium;
  onChangeColumnModel?: (v: GridInitialStatePremium['columns']) => void;
  onColumnWidthChange?: DataGridPremiumProps['onColumnWidthChange'];
  onColumnOrderChange?: DataGridPremiumProps['onColumnOrderChange'];
  onChangeOrderPosition?: (v: GridRowModel[]) => void;
  onRowsCountChange?: (
    rows: GridRowModel[],
    prevRows: GridRowModel[],
    apiRef: DataGridPremiumProps['apiRef'],
    potentialNextColumnName?: string,
  ) => void;
  /* Returning `true` tells the table that the keypress requested a brand-new
  row, so the cell to focus next does not exist yet — see
  `beginRowFocusHandoff`. */
  onCellKeyDown?: (
    params: GridCellParams,
    event: MuiEvent<React.KeyboardEvent>,
    details: GridCallbackDetails,
    apiRef: DataGridPremiumProps['apiRef'],
  ) => boolean | void;
  detailRowContent?: {
    content: ReactElement;
    clickableRow?: boolean; // if true subrow expands when row is clicked
    height?: number | 'auto';
  };
  disableRowReorder?: string[];
  focusTabProps?: IFocusTabProps;
  columns?: GridColDef[];
  footer?: ReactElement | null;
  externalApiRef?: MutableRefObject<GridApiPremium>;
  isGroupTable?: boolean;
  enableSingleClickEditing?: boolean;
  isLongLoading?: boolean;
  messagesForLongLoading?: MessageRotatorProps['uniqueMessages'];
  rowDataAttr?: (row: GridRowModel) => string;
}
type TypeFixedHeight = ICommonTable & { height?: number; heightOffset?: never };
type TypeOffsetHeight = ICommonTable & { heightOffset?: number; height?: never };

export type TypeTableHeight = TypeFixedHeight | TypeOffsetHeight;

type TypeArrowNavigationOn = TypeTableHeight & {
  arrowNavigation?: TypeArrowNavigation;
  customArrowHandler?: TypeArrowNavigationFromEditable;
  onRowClick: TypeTableHeight['onRowClick'];
  rowSelectionModel: TypeTableHeight['rowSelectionModel'];
};
type TypeArrowNavigationOff = TypeTableHeight & {
  arrowNavigation?: never;
  customArrowHandler?: never;
};

type TypeArrowNavigation =
  | boolean
  | {
      condition?: (row: GridRowModel) => boolean;
    };

type TypeArrowNavigationFromEditable =
  | boolean
  | {
      handleNextRow?: (args: {
        nextRow?: GridRowModel;
        creatingNewRow?: boolean;
        cellMode: GridCellMode;
      }) => void;
      ignoreVirtualPositionInBottom?: boolean;
    };

export type TypeTable = TypeArrowNavigationOn | TypeArrowNavigationOff;

export interface IRowStyles {
  className: string;
  bgColor?: string;
  bgColorHover?: string;
  bgColorSelected?: string;
  bgColorSelectedHover?: string;
  rowStyles?: SxProps;
}

export interface ICellStyles extends Pick<IRowStyles, 'className'> {
  cellStyles: SxProps;
}

export interface IColumnStateFields {
  model: GridColumnsInitialState | undefined;
  state: boolean;
}

export interface IFocusTabProps extends Pick<IUseTabNavigation['params'], 'disabledRowsFocus'> {
  enabled?: boolean;
  enabledFields?: string[];
}

type TypeFindNextRowFunction = (
  code: 'ArrowUp' | 'ArrowDown',
  rows: GridRowModel[],
  foundIndex: number,
  condition?: (row: GridRowModel) => boolean,
) => GridRowModel | undefined;

export type TDetailRow = Partial<GridRowParams>;
type CellEditStopEvent = MuiEvent<MuiBaseEvent> & { detail?: number };
